import { useState, useEffect } from 'react';
import { tryoutAdminAPI, subjectAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Trash2, Settings, Sparkles, CheckCircle, AlertTriangle, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function SubjectConfigDialog({ 
  open, 
  onOpenChange, 
  tryout, 
  onSaved 
}) {
  const [subjects, setSubjects] = useState([]);
  const [config, setConfig] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open && tryout) {
      loadSubjects();
      loadStats();
      // Initialize config from tryout
      const existingConfig = tryout.subject_config || tryout.time_limits || [];
      if (existingConfig.length > 0) {
        setConfig(existingConfig.map(sc => ({
          mapel: sc.mapel,
          question_count: sc.question_count || 0,
          duration_minutes: sc.duration_minutes || 0
        })));
      }
    }
  }, [open, tryout]);

  const loadSubjects = async () => {
    try {
      const res = await subjectAPI.getAll(tryout.category);
      setSubjects(res.data || []);
      
      // If no config, initialize with subjects
      const existingConfig = tryout.subject_config || tryout.time_limits || [];
      if (existingConfig.length === 0 && res.data.length > 0) {
        setConfig(res.data.map(s => ({
          mapel: s.name,
          question_count: 0,
          duration_minutes: 0
        })));
      }
    } catch (error) {
      console.error('Failed to load subjects:', error);
    }
  };

  const loadStats = async () => {
    try {
      const res = await tryoutAdminAPI.getQuestionStats(tryout.id);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const updateConfig = (index, field, value) => {
    const newConfig = [...config];
    newConfig[index] = { ...newConfig[index], [field]: parseInt(value) || 0 };
    setConfig(newConfig);
  };

  const addSubject = () => {
    setConfig([...config, { mapel: '', question_count: 0, duration_minutes: 0 }]);
  };

  const removeSubject = (index) => {
    setConfig(config.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Validate
    const validConfig = config.filter(c => c.mapel && c.question_count > 0);
    if (validConfig.length === 0) {
      toast.error('Tambahkan minimal satu mapel dengan jumlah soal > 0');
      return;
    }

    try {
      setLoading(true);
      await tryoutAdminAPI.setSubjectConfig(tryout.id, { subject_config: validConfig });
      toast.success('Konfigurasi mapel berhasil disimpan');
      loadStats();
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan konfigurasi');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await tryoutAdminAPI.generateQuestions(tryout.id);
      toast.success(`${res.data.total_questions} soal berhasil di-generate`);
      loadStats();
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal generate soal');
    } finally {
      setGenerating(false);
    }
  };

  const totalQuestions = config.reduce((sum, c) => sum + (c.question_count || 0), 0);
  const totalDuration = config.reduce((sum, c) => sum + (c.duration_minutes || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Konfigurasi Mapel
          </DialogTitle>
          <DialogDescription>
            Atur jumlah soal per mapel untuk Try Out: <strong>{tryout?.title}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Stats Card */}
          {stats && (
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-[#4472C4]">{stats.current_questions}</div>
                    <div className="text-xs text-slate-500">Soal Saat Ini</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{stats.total_available}</div>
                    <div className="text-xs text-slate-500">Bank Soal Tersedia</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-orange-500">{totalQuestions}</div>
                    <div className="text-xs text-slate-500">Total Dikonfigurasi</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Subject Config List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Konfigurasi per Mapel</Label>
              <Button size="sm" variant="outline" onClick={addSubject}>
                <Plus className="w-4 h-4 mr-1" />
                Tambah Mapel
              </Button>
            </div>

            {config.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Belum ada konfigurasi mapel</p>
              </div>
            ) : (
              <div className="space-y-2">
                {config.map((item, index) => {
                  const subjectStat = stats?.by_subject?.find(s => s.subject === item.mapel);
                  const isSufficient = subjectStat ? subjectStat.available >= item.question_count : true;
                  
                  return (
                    <Card key={index} className={`border ${!isSufficient ? 'border-orange-300 bg-orange-50' : ''}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <select
                              value={item.mapel}
                              onChange={(e) => updateConfig(index, 'mapel', e.target.value)}
                              className="w-full px-3 py-2 border rounded-md text-sm"
                            >
                              <option value="">Pilih Mapel</option>
                              {subjects.map(s => (
                                <option key={s.id} value={s.name}>{s.name} - {s.full_name}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              value={item.question_count}
                              onChange={(e) => updateConfig(index, 'question_count', e.target.value)}
                              placeholder="Jumlah"
                              className="text-center"
                            />
                            <div className="text-xs text-slate-500 text-center mt-1">Soal</div>
                          </div>
                          
                          <div className="w-24">
                            <Input
                              type="number"
                              min="0"
                              value={item.duration_minutes}
                              onChange={(e) => updateConfig(index, 'duration_minutes', e.target.value)}
                              placeholder="Menit"
                              className="text-center"
                            />
                            <div className="text-xs text-slate-500 text-center mt-1">Menit</div>
                          </div>

                          {subjectStat && (
                            <div className="w-20 text-center">
                              {isSufficient ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {subjectStat.available}
                                </Badge>
                              ) : (
                                <Badge className="bg-orange-100 text-orange-700">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {subjectStat.available}
                                </Badge>
                              )}
                              <div className="text-xs text-slate-500 mt-1">Tersedia</div>
                            </div>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeSubject(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Summary */}
          {config.length > 0 && (
            <Card className="bg-[#4472C4]/5 border-[#4472C4]/20">
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-slate-600">Total Konfigurasi:</div>
                    <div className="text-lg font-bold text-[#4472C4]">
                      {totalQuestions} Soal • {totalDuration} Menit
                    </div>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    {config.filter(c => c.mapel && c.question_count > 0).length} Mapel
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading}
            className="bg-[#4472C4] hover:bg-[#3461B3]"
          >
            {loading ? 'Menyimpan...' : 'Simpan Konfigurasi'}
          </Button>
          <Button 
            onClick={handleGenerate} 
            disabled={generating || totalQuestions === 0}
            className="bg-green-600 hover:bg-green-700"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {generating ? 'Generating...' : 'Generate Soal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
